import React, { useState, Dispatch, SetStateAction } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as SelectedIcon } from "src/images/selected.svg";
import { ReactComponent as FieldIcon } from "src/images/field.svg";
import { ReactComponent as GroupIcon } from "src/images/group.svg";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import TasksTableFilters from "./TasksTableFIlters";
import { ReactComponent as RectangleCheckIcon } from "src/images/rectangle-check.svg";
import { TableSettingsDropdown } from "src/app/dropdowns/TableSettingsDropdown/TableSettingsDropdown";
import { Button } from "src/app/components/Button/Button";
import { ReactComponent as MagnifyingGlass } from "src/images/magnifier-grey.svg";
import { TASK_FIELDS_BLACKLIST } from "src/app/Task/components/TaskMetaFields/TaskMetaFields";
import { ReactComponent as CreatorIcon } from "src/images/creator.svg";
import { ReactComponent as PaymentIcon } from "src/images/payment.svg";
import { ReactComponent as PictureIcon } from "src/images/picture.svg";
import { ReactComponent as MyTasksIcon } from "src/images/menu-icons/my-tasks.svg";
import { ReactComponent as MessageIcon } from "src/images/menu-icons/message.svg";
import { useCheckFeatureAccess } from "src/app/subscription/subscriptionFunctions";
import { SubscriptionEntitlements } from "src/app/subscription/subscriptionUtils";
import {
  openAddCreatorsModal,
  openAddPublicationModal,
  openAddTaskModal,
  setPlanUpgradeNeededModalContext,
} from "src/redux";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { TaskType, ColumnOption } from "src/types";
import { RootState } from "src/redux/reducers";
import useCheckProjectPermission from "src/hooks/useCheckProjectPermission";
import { ReactComponent as PlusIcon } from "src/images/plus-transparent.svg";
import { Metadata } from "src/app/Task/Subtask/Subtask";
import { HIDDEN_FIELDS } from "src/utils/variables";
import { Pane } from "../../../components/Pane/Pane";
import { CurrentViewValue } from "../types";
import { IColumn } from "../../Project";
import TaskSortFieldsDropdown from "./TaskSortFieldsDropdown";
import { setShowTableSearch } from "src/redux/main/mainActions";
import DropdownPortal from "src/app/components/DropdownPortal";

interface SingleProjectToolbarProps {
  setShowAccessLinkModal: Dispatch<SetStateAction<boolean>>;
  setBoardHidden: Dispatch<SetStateAction<boolean>>;
  setSortingColumn: Dispatch<SetStateAction<ColumnOption | null>>;
  setShowAddTaskFieldModal: Dispatch<SetStateAction<boolean>>;
  setCurrentView: Dispatch<SetStateAction<CurrentViewValue>>;
  currentView: CurrentViewValue;
  sortingColumn: ColumnOption | null;
  columnOptions: ColumnOption[];
  taskFields: IColumn[];
  setShowTaskFieldSetupModal: Dispatch<SetStateAction<boolean>>;
  editedFieldData: any;
  setEditedFieldData: Dispatch<SetStateAction<any>>;
}

export default function SingleProjectToolbar({
  setShowAccessLinkModal,
  setBoardHidden,
  setSortingColumn,
  setShowAddTaskFieldModal,
  setCurrentView,
  currentView,
  sortingColumn,
  columnOptions,
  taskFields,
  setShowTaskFieldSetupModal,
  editedFieldData,
  setEditedFieldData,
}: SingleProjectToolbarProps) {
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [dropdownMenuVisible, setDropdownMenuVisible] = useState(false);

  const dispatch = useDispatch();

  const hasTaskManagementPermission =
    useCheckProjectPermission("TASK_MANAGEMENT");

  const hasAccessToForms = useCheckFeatureAccess(
    SubscriptionEntitlements.Forms,
  );

  const taskType = useSelector(
    (state: RootState) => state.projectReducer.taskType,
  );
  const tasks = useSelector((state: RootState) => state.taskReducer.tasks);
  const selectedFilters = useSelector(
    (state: RootState) => state.taskFiltersReducer.selectedFilters,
  );
  const tasksColumns = useSelector(
    (state: RootState) => state.taskReducer.tasksColumns,
  );

  const handleOpenForm = () => {
    if (hasAccessToForms) {
      setShowAccessLinkModal(true);
    } else {
      dispatch(
        setPlanUpgradeNeededModalContext(SubscriptionEntitlements.Forms),
      );
    }
  };

  const handleAddTaskModal = () => {
    setDropdownMenuVisible(false);
    switch (taskType) {
      case TaskType.Creator:
        dispatch(openAddCreatorsModal());
        break;

      case TaskType.Publication:
        dispatch(openAddPublicationModal());
        break;

      default:
        dispatch(openAddTaskModal());
        break;
    }
  };

  const getTaskTypeIcon = () => {
    switch (taskType) {
      case TaskType.Creator:
        return <CreatorIcon />;
      case TaskType.Content:
        return <PictureIcon />;
      case TaskType.Action:
        return <MyTasksIcon />;
      case TaskType.Publication:
        return <MessageIcon />;
      case TaskType.Payment:
        return <PaymentIcon />;
      default:
        return null;
    }
  };

  const changeTableData = (newValue: ColumnOption) => {
    setBoardHidden(true);
    setSortingColumn((state: ColumnOption | null) => {
      if (!state) return newValue;

      return {
        ...state,
        uuid: newValue.uuid,
        label: newValue.label,
        type: newValue.type,
      };
    });

    setTimeout(() => {
      setSortingColumn(newValue);
      setBoardHidden(false);
    }, 200);
  };

  const dropdownMenu = (
    <DropdownMenu isOpen={dropdownMenuVisible}>
      <DropdownMenuItem
        className="projects__topbar-right-add-button"
        onClick={handleAddTaskModal}
      >
        {getTaskTypeIcon()}
        <IDHFormattedMessage
          id={`ws_${taskType.toLowerCase()}`}
          defaultMessage={taskType}
        />
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          setShowAddTaskFieldModal(true);
          setDropdownMenuVisible(false);
        }}
      >
        <FieldIcon />
        <IDHFormattedMessage
          id="ws_table_column"
          defaultMessage="Table column"
        />
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          handleOpenForm();
          setDropdownMenuVisible(false);
        }}
      >
        <RectangleCheckIcon />
        <IDHFormattedMessage id="ws_form" defaultMessage="Form" />
      </DropdownMenuItem>
    </DropdownMenu>
  );

  return (
    <div className="projects__topbar-toolbar">
      {hasTaskManagementPermission && (
        <DropdownPortal
          overlay={dropdownMenu}
          visible={dropdownMenuVisible}
          onVisibleChange={(v: boolean) => setDropdownMenuVisible(v)}
        >
          <Button size="small" textWithIcon variant="blue">
            <PlusIcon />
            <IDHFormattedMessage id="ws_add" defaultMessage="Add" />
          </Button>
        </DropdownPortal>
      )}

      {currentView === "board" && (
        <DropdownPortal
          overlay={
            <DropdownMenu>
              {columnOptions.length ? (
                columnOptions.map((option, index) => (
                  <DropdownMenuItem
                    key={`${option.value}-${index}`}
                    dark={option.value === sortingColumn?.value}
                    onClick={() => changeTableData(option)}
                  >
                    {option.label}

                    {option.value === sortingColumn?.value && (
                      <SelectedIcon style={{ marginLeft: 8, marginRight: 0 }} />
                    )}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem>
                  <IDHFormattedMessage
                    id="ws_nothing_to_sort"
                    defaultMessage="Nothing to sort"
                  />
                </DropdownMenuItem>
              )}
            </DropdownMenu>
          }
        >
          <Button size="small" textWithIcon variant="white">
            <GroupIcon />
            <IDHFormattedMessage id="ws_group_by" defaultMessage="Group by" />
            :&nbsp;
            {sortingColumn?.label}
          </Button>
        </DropdownPortal>
      )}

      {currentView === "list" && (
        <>
          {tasks.length > 0 && (
            <>
              <Pane
                showPane={filtersVisible}
                hidePane={() => setFiltersVisible(false)}
                width={309}
              >
                <TasksTableFilters
                  setFiltersVisible={setFiltersVisible}
                  taskType={taskType}
                  data={tasks}
                />
              </Pane>
              <Button
                variant="white"
                textWithIcon
                size="small"
                onClick={() => setFiltersVisible((prev) => !prev)}
                data-qa="filter-button"
              >
                <FilterIcon />
                <span>
                  <IDHFormattedMessage id="ws_filter" defaultMessage="Filter" />
                  {Object.keys(selectedFilters).length > 0 &&
                    `: ${Object.keys(selectedFilters).length}`}
                </span>
              </Button>
            </>
          )}
          {tasks.length > 0 && (
            <TaskSortFieldsDropdown
              fields={taskFields
                ?.filter((field) => {
                  const tasksMetaData = tasks[0]?.metadata;
                  return tasksMetaData?.find(
                    (metafield: Metadata) =>
                      metafield.uuid === field.id &&
                      !HIDDEN_FIELDS.includes(field.name),
                  );
                })
                ?.map((field) => ({
                  ...field,
                  title: field.name,
                }))}
            />
          )}
        </>
      )}

      {/* TODO: https://app.asana.com/0/807220246535156/1208973385070612/f */}
      {/* <TableViewSelector */}
      {/*  wsTableViews={projectBasicData.wsTableViews} */}
      {/*  selectedWsTableView={{ */}
      {/*    label: "Custom", */}
      {/*    value: null, */}
      {/*  }} */}
      {/*  setSelectedWsTableView={(newValue: any) => {}} */}
      {/*  setFilters={(newFilters: any) => {}} */}
      {/*  setFilter={(uuid: string, value: any) => {}} */}
      {/*  setHiddenOrVisibleColumnIds={(columnIds: any) => {}} */}
      {/*  setSortBy={(newValue: any) => {}} */}
      {/*  setSort={(newValue: any) => {}} */}
      {/*  resetToDefaultSettings={null} */}
      {/*  columnIds={[]} */}
      {/* /> */}
      {/* {identity?.permissions?.workspace?.includes( */}
      {/*  "manage_table_views", */}
      {/* ) && ( */}
      {/*  <Button */}
      {/*    variant="white" */}
      {/*    size="small" */}
      {/*    textWithIcon */}
      {/*    onClick={() => setShowSaveTableViewModal(true)} */}
      {/*  > */}
      {/*    <DownloadIcon /> */}
      {/*    <IDHFormattedMessage */}
      {/*      id="ws_save_view" */}
      {/*      defaultMessage="Save view" */}
      {/*    /> */}
      {/*  </Button> */}
      {/* )} */}

      <Button
        variant="white"
        size="small"
        textWithIcon
        onClick={() => dispatch(setShowTableSearch(true))}
      >
        <MagnifyingGlass />
        <IDHFormattedMessage id="ws_search" defaultMessage="Search" />
      </Button>

      <TableSettingsDropdown
        setShowFieldSetupModal={setShowTaskFieldSetupModal}
        fieldData={editedFieldData}
        setFieldData={setEditedFieldData}
        fields={tasksColumns
          .filter(
            (field) => !TASK_FIELDS_BLACKLIST.includes(field.metaFieldKey),
          )
          .map((column) => ({
            id: column.metaFieldId,
            name: column.metaFieldName,
            isRequired: column.metaFieldIsRequired,
            isVisible: column.metaFieldVisible,
            key: column.metaFieldKey,
            metaFieldOptions: column.data,
            metaFieldSource: column.metaFieldSource,
            rank: column.metaFieldRank,
            title: column.metaFieldName,
            type: column.metaFieldType,
            valueSource: column.metaFieldValueSource,
          }))} // tasksColumns are mapped because old CustomizeFieldsDropdown component required this object template
        currentView={currentView}
        setCurrentView={setCurrentView}
      />
    </div>
  );
}
